import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { 
  Play, Pause, SkipBack, SkipForward, Upload, Wand2, 
  RefreshCw, Check, AlertCircle, Loader2, Film, Image as ImageIcon,
  Plus, X, User, Palette, ChevronLeft, ChevronRight, Maximize2
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Motion Lab v2 - Reference-Based Keyframe Generation
 * 
 * Flow:
 * 1. Upload character references + background
 * 2. Describe the action
 * 3. AI breaks down action into keyframes
 * 4. AI generates all keyframes from references
 * 5. Preview, regenerate, interpolate
 */
export default function MotionLab() {
  // Mode: 'setup' | 'breakdown' | 'keyframes' | 'preview'
  const [mode, setMode] = useState('setup');
  
  // Character references: { name: string, images: [{file, preview, url}] }
  const [characters, setCharacters] = useState([
    { name: '', images: [] }
  ]);
  
  // Background/scene references
  const [backgroundImages, setBackgroundImages] = useState([]);
  
  // Action prompt
  const [actionPrompt, setActionPrompt] = useState('');
  const [duration, setDuration] = useState(2);
  const [keyframesPerSecond, setKeyframesPerSecond] = useState(2);
  
  // Job state
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState('idle');
  const [actionBreakdown, setActionBreakdown] = useState([]);
  const [generatedKeyframes, setGeneratedKeyframes] = useState([]);
  const [generatingIndex, setGeneratingIndex] = useState(null);
  
  // Lightbox/carousel state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  // Preview state
  const [finalFrames, setFinalFrames] = useState([]);
  const [currentFrame, setCurrentFrame] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [finalVideoUrl, setFinalVideoUrl] = useState(null);
  
  // Error
  const [error, setError] = useState(null);
  
  const playbackRef = useRef(null);

  // Add a new character slot
  const addCharacter = () => {
    setCharacters([...characters, { name: '', images: [] }]);
  };

  // Remove a character
  const removeCharacter = (index) => {
    setCharacters(characters.filter((_, i) => i !== index));
  };

  // Update character name
  const updateCharacterName = (index, name) => {
    const updated = [...characters];
    updated[index].name = name;
    setCharacters(updated);
  };

  // Add image to character
  const addCharacterImage = (charIndex, e) => {
    const files = Array.from(e.target.files);
    const updated = [...characters];
    
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        updated[charIndex].images.push({
          file,
          preview: event.target.result,
          url: null // Will be set after S3 upload
        });
        setCharacters([...updated]);
      };
      reader.readAsDataURL(file);
    });
  };

  // Remove image from character
  const removeCharacterImage = (charIndex, imgIndex) => {
    const updated = [...characters];
    updated[charIndex].images = updated[charIndex].images.filter((_, i) => i !== imgIndex);
    setCharacters(updated);
  };

  // Add background image
  const addBackgroundImage = (e) => {
    const files = Array.from(e.target.files);
    
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        setBackgroundImages(prev => [...prev, {
          file,
          preview: event.target.result,
          url: null
        }]);
      };
      reader.readAsDataURL(file);
    });
  };

  // Remove background image
  const removeBackgroundImage = (index) => {
    setBackgroundImages(prev => prev.filter((_, i) => i !== index));
  };

  // Upload image to S3
  const uploadImageToS3 = async (dataUrl, filename) => {
    const resp = await axios.post(`${API}/api/motion-lab/upload-image`, {
      image_data: dataUrl,
      filename: filename
    });
    return resp.data.url;
  };

  // Start the generation process
  const startGeneration = async () => {
    if (!actionPrompt.trim()) {
      setError('Please enter an action description');
      return;
    }

    const validCharacters = characters.filter(c => c.name && c.images.length > 0);
    if (validCharacters.length === 0) {
      setError('Please add at least one character with reference images');
      return;
    }

    setError(null);
    setJobStatus('uploading');

    try {
      // Upload all images to S3
      const uploadedCharacters = [];
      for (const char of validCharacters) {
        const uploadedImages = [];
        for (let i = 0; i < char.images.length; i++) {
          const img = char.images[i];
          if (!img.url) {
            const url = await uploadImageToS3(img.preview, `${char.name}_ref_${i}.png`);
            uploadedImages.push(url);
          } else {
            uploadedImages.push(img.url);
          }
        }
        uploadedCharacters.push({
          name: char.name,
          image_urls: uploadedImages
        });
      }

      // Upload background images
      const uploadedBackgrounds = [];
      for (let i = 0; i < backgroundImages.length; i++) {
        const img = backgroundImages[i];
        if (!img.url) {
          const url = await uploadImageToS3(img.preview, `background_${i}.png`);
          uploadedBackgrounds.push(url);
        } else {
          uploadedBackgrounds.push(img.url);
        }
      }

      setJobStatus('creating');

      // Create the job
      const createResp = await axios.post(`${API}/api/motion-lab/v2/create-job`, {
        characters: uploadedCharacters,
        background_urls: uploadedBackgrounds,
        action_prompt: actionPrompt,
        duration_seconds: duration,
        keyframes_per_second: keyframesPerSecond
      });

      const newJobId = createResp.data.job_id;
      setJobId(newJobId);
      setJobStatus('breaking_down');

      // Generate action breakdown
      const breakdownResp = await axios.post(
        `${API}/api/motion-lab/v2/job/${newJobId}/generate-breakdown`
      );

      setActionBreakdown(breakdownResp.data.breakdown);
      setJobStatus('breakdown_ready');
      setMode('breakdown');

    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create job');
      setJobStatus('error');
    }
  };

  // Generate keyframes one by one with live updates
  const generateAllKeyframes = async () => {
    if (!jobId) return;

    setJobStatus('generating_keyframes');
    setMode('keyframes');

    try {
      // Initialize keyframes from breakdown
      const jobResp = await axios.get(`${API}/api/motion-lab/v2/job/${jobId}`);
      const keyframes = jobResp.data.keyframes || [];
      setGeneratedKeyframes(keyframes);

      // Generate each keyframe one by one
      for (let i = 0; i < keyframes.length; i++) {
        if (keyframes[i].status === 'complete') continue;
        
        setGeneratingIndex(i);
        
        // Update local state to show generating
        setGeneratedKeyframes(prev => prev.map((k, idx) => 
          idx === i ? { ...k, status: 'generating' } : k
        ));

        try {
          const resp = await axios.post(
            `${API}/api/motion-lab/v2/job/${jobId}/regenerate-keyframe/${i}`
          );

          // Update with the result
          setGeneratedKeyframes(prev => prev.map((k, idx) => 
            idx === i ? { ...k, status: 'complete', url: resp.data.url } : k
          ));
        } catch (err) {
          // Mark as error but continue with others
          setGeneratedKeyframes(prev => prev.map((k, idx) => 
            idx === i ? { ...k, status: 'error', error: err.message } : k
          ));
        }
      }

      setGeneratingIndex(null);
      setJobStatus('keyframes_complete');

    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate keyframes');
      setJobStatus('error');
    }
  };

  // Regenerate a single keyframe
  const regenerateKeyframe = async (index) => {
    if (!jobId) return;

    setGeneratingIndex(index);
    
    // Update local state to show generating
    setGeneratedKeyframes(prev => prev.map((k, idx) => 
      idx === index ? { ...k, status: 'generating' } : k
    ));

    try {
      const resp = await axios.post(
        `${API}/api/motion-lab/v2/job/${jobId}/regenerate-keyframe/${index}`
      );

      // Update with result
      setGeneratedKeyframes(prev => prev.map((k, idx) => 
        idx === index ? { ...k, status: 'complete', url: resp.data.url } : k
      ));

    } catch (err) {
      setGeneratedKeyframes(prev => prev.map((k, idx) => 
        idx === index ? { ...k, status: 'error' } : k
      ));
      setError(err.response?.data?.detail || 'Failed to regenerate keyframe');
    } finally {
      setGeneratingIndex(null);
    }
  };

  // Lightbox controls
  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const nextImage = () => {
    setLightboxIndex((prev) => (prev + 1) % generatedKeyframes.length);
  };

  const prevImage = () => {
    setLightboxIndex((prev) => (prev - 1 + generatedKeyframes.length) % generatedKeyframes.length);
  };

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (!lightboxOpen) return;
    
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, generatedKeyframes.length]);

  // Regenerate from lightbox
  const regenerateFromLightbox = async () => {
    await regenerateKeyframe(lightboxIndex);
  };

  // Run frame interpolation using FILM model
  const runInterpolation = async () => {
    if (!jobId) return;

    setJobStatus('interpolating');
    setError(null);

    try {
      // times_to_interpolate: 3 = 7 frames between keyframes, 4 = 15 frames
      const resp = await axios.post(
        `${API}/api/motion-lab/v2/job/${jobId}/interpolate-frames`,
        null,
        { 
          params: { times_to_interpolate: 3 },
          timeout: 300000 // 5 min timeout for FILM processing
        }
      );

      const jobResp = await axios.get(`${API}/api/motion-lab/v2/job/${jobId}`);
      setFinalFrames(jobResp.data.final_frames || []);
      setJobStatus('interpolated');
      setMode('preview');

    } catch (err) {
      setError(err.response?.data?.detail || 'Interpolation failed');
      setJobStatus('error');
    }
  };

  // Compile to video
  const compileVideo = async () => {
    if (!jobId) return;

    setJobStatus('compiling');

    try {
      await axios.post(`${API}/api/motion-lab/v2/job/${jobId}/compile-video`);

      const jobResp = await axios.get(`${API}/api/motion-lab/v2/job/${jobId}`);
      setFinalVideoUrl(jobResp.data.final_video_url);
      setJobStatus('complete');

    } catch (err) {
      setError(err.response?.data?.detail || 'Video compilation failed');
      setJobStatus('error');
    }
  };

  // Playback controls
  useEffect(() => {
    if (isPlaying && finalFrames.length > 0) {
      playbackRef.current = setInterval(() => {
        setCurrentFrame(prev => {
          const next = prev + 1;
          if (next >= finalFrames.length) {
            setIsPlaying(false);
            return 0;
          }
          return next;
        });
      }, 1000 / 24);
    } else {
      clearInterval(playbackRef.current);
    }
    return () => clearInterval(playbackRef.current);
  }, [isPlaying, finalFrames.length]);

  // Stats
  const totalKeyframes = Math.floor(duration * keyframesPerSecond);
  const completedKeyframes = generatedKeyframes.filter(k => k.status === 'complete').length;

  return (
    <div className="min-h-screen bg-zinc-950 text-white p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2 flex items-center gap-3">
            <Film className="w-8 h-8 text-purple-500" />
            Motion Lab
            <Badge variant="outline" className="text-xs">v2 - REFERENCE BASED</Badge>
          </h1>
          <p className="text-zinc-400">
            Upload character references, describe the action, AI generates all keyframes
          </p>
        </div>

        {/* Error display */}
        {error && (
          <div className="mb-6 p-4 bg-red-900/30 border border-red-500 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-500 shrink-0" />
            <span>{error}</span>
            <Button variant="ghost" size="sm" onClick={() => setError(null)} className="ml-auto">
              Dismiss
            </Button>
          </div>
        )}

        {/* Mode Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          <Button 
            variant={mode === 'setup' ? 'default' : 'outline'} 
            onClick={() => setMode('setup')}
            size="sm"
          >
            1. Setup
          </Button>
          <Button 
            variant={mode === 'breakdown' ? 'default' : 'outline'} 
            onClick={() => setMode('breakdown')}
            disabled={actionBreakdown.length === 0}
            size="sm"
          >
            2. Breakdown
          </Button>
          <Button 
            variant={mode === 'keyframes' ? 'default' : 'outline'} 
            onClick={() => setMode('keyframes')}
            disabled={generatedKeyframes.length === 0}
            size="sm"
          >
            3. Keyframes
          </Button>
          <Button 
            variant={mode === 'preview' ? 'default' : 'outline'} 
            onClick={() => setMode('preview')}
            disabled={finalFrames.length === 0}
            size="sm"
          >
            4. Preview
          </Button>
        </div>

        {/* SETUP MODE */}
        {mode === 'setup' && (
          <div className="space-y-6">
            {/* Characters */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-lg flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <User className="w-5 h-5" />
                    Character References
                  </span>
                  <Button variant="outline" size="sm" onClick={addCharacter}>
                    <Plus className="w-4 h-4 mr-1" /> Add Character
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {characters.map((char, charIndex) => (
                  <div key={charIndex} className="p-4 bg-zinc-800 rounded-lg space-y-3">
                    <div className="flex items-center gap-2">
                      <Input
                        placeholder="Character name (e.g., Dean)"
                        value={char.name}
                        onChange={(e) => updateCharacterName(charIndex, e.target.value)}
                        className="flex-1"
                      />
                      {characters.length > 1 && (
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => removeCharacter(charIndex)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    
                    <div className="flex gap-2 flex-wrap">
                      {char.images.map((img, imgIndex) => (
                        <div key={imgIndex} className="relative w-20 h-20 group">
                          <img 
                            src={img.preview} 
                            alt={`${char.name} ref ${imgIndex}`}
                            className="w-full h-full object-cover rounded"
                          />
                          <button
                            onClick={() => removeCharacterImage(charIndex, imgIndex)}
                            className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                      
                      <label className="w-20 h-20 border-2 border-dashed border-zinc-600 rounded flex items-center justify-center cursor-pointer hover:border-purple-500 transition-colors">
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={(e) => addCharacterImage(charIndex, e)}
                          className="hidden"
                        />
                        <Plus className="w-6 h-6 text-zinc-500" />
                      </label>
                    </div>
                    
                    <p className="text-xs text-zinc-500">
                      Add multiple angles/poses of this character for better consistency
                    </p>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Background */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Palette className="w-5 h-5" />
                  Background / Scene References (Optional)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2 flex-wrap">
                  {backgroundImages.map((img, index) => (
                    <div key={index} className="relative w-24 h-16 group">
                      <img 
                        src={img.preview} 
                        alt={`Background ${index}`}
                        className="w-full h-full object-cover rounded"
                      />
                      <button
                        onClick={() => removeBackgroundImage(index)}
                        className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  
                  <label className="w-24 h-16 border-2 border-dashed border-zinc-600 rounded flex items-center justify-center cursor-pointer hover:border-purple-500 transition-colors">
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={addBackgroundImage}
                      className="hidden"
                    />
                    <Plus className="w-6 h-6 text-zinc-500" />
                  </label>
                </div>
              </CardContent>
            </Card>

            {/* Action Description */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-lg">Action Description</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Textarea
                  placeholder="Describe the action sequence (e.g., 'Dean lands a powerful right hook on Buk, who staggers backward')"
                  value={actionPrompt}
                  onChange={(e) => setActionPrompt(e.target.value)}
                  rows={3}
                  className="text-base"
                />

                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="text-sm text-zinc-400 mb-2 block">
                      Duration: {duration} seconds
                    </label>
                    <Slider
                      value={[duration]}
                      onValueChange={([v]) => setDuration(v)}
                      min={1}
                      max={5}
                      step={0.5}
                    />
                  </div>
                  <div>
                    <label className="text-sm text-zinc-400 mb-2 block">
                      Keyframes per second: {keyframesPerSecond}
                    </label>
                    <Slider
                      value={[keyframesPerSecond]}
                      onValueChange={([v]) => setKeyframesPerSecond(v)}
                      min={1}
                      max={4}
                      step={1}
                    />
                  </div>
                </div>

                <div className="p-4 bg-zinc-800 rounded-lg">
                  <p className="text-sm">
                    This will generate <strong className="text-purple-400">{totalKeyframes} keyframes</strong> for a {duration}s animation.
                  </p>
                </div>

                <Button 
                  className="w-full" 
                  size="lg"
                  onClick={startGeneration}
                  disabled={jobStatus === 'uploading' || jobStatus === 'creating' || jobStatus === 'breaking_down'}
                >
                  {jobStatus === 'uploading' ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading Images...</>
                  ) : jobStatus === 'creating' ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating Job...</>
                  ) : jobStatus === 'breaking_down' ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing Action...</>
                  ) : (
                    <><Wand2 className="w-4 h-4 mr-2" /> Generate Keyframes</>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* BREAKDOWN MODE */}
        {mode === 'breakdown' && (
          <div className="space-y-6">
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-lg">Action Breakdown</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-zinc-400">
                  AI has broken down your action into {actionBreakdown.length} keyframes. 
                  Review and edit if needed, then generate.
                </p>

                <div className="space-y-2">
                  {actionBreakdown.map((frame, index) => (
                    <div key={index} className="p-3 bg-zinc-800 rounded-lg flex items-start gap-3">
                      <span className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center text-sm shrink-0">
                        {index + 1}
                      </span>
                      <div className="flex-1">
                        <p className="text-sm">{frame.description}</p>
                        <p className="text-xs text-zinc-500 mt-1">
                          t={frame.timestamp}s
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                <Button 
                  className="w-full" 
                  size="lg"
                  onClick={generateAllKeyframes}
                  disabled={jobStatus === 'generating_keyframes'}
                >
                  {jobStatus === 'generating_keyframes' ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating Keyframes...</>
                  ) : (
                    <><Wand2 className="w-4 h-4 mr-2" /> Generate All Keyframes</>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* KEYFRAMES MODE */}
        {mode === 'keyframes' && (
          <div className="space-y-6">
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="py-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-zinc-400">
                    {generatingIndex !== null ? (
                      <>Generating keyframe {generatingIndex + 1} of {generatedKeyframes.length}...</>
                    ) : (
                      <>Progress: {completedKeyframes} / {generatedKeyframes.length} keyframes</>
                    )}
                  </span>
                  <Button
                    variant="secondary"
                    onClick={runInterpolation}
                    disabled={completedKeyframes < generatedKeyframes.length || jobStatus === 'interpolating'}
                  >
                    {jobStatus === 'interpolating' ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Interpolating...</>
                    ) : (
                      <><Film className="w-4 h-4 mr-2" /> Create Animation</>
                    )}
                  </Button>
                </div>
                <div className="w-full bg-zinc-800 rounded-full h-2">
                  <div 
                    className="bg-purple-600 h-2 rounded-full transition-all"
                    style={{ width: `${(completedKeyframes / Math.max(1, generatedKeyframes.length)) * 100}%` }}
                  />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-lg">Generated Keyframes</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {generatedKeyframes.map((frame, index) => (
                    <div key={index} className="space-y-2">
                      <div 
                        className={`aspect-video rounded-lg overflow-hidden border-2 cursor-pointer relative group ${
                          frame.status === 'complete' ? 'border-green-500 hover:border-green-400' :
                          frame.status === 'generating' ? 'border-yellow-500' :
                          frame.status === 'error' ? 'border-red-500' :
                          'border-zinc-700'
                        }`}
                        onClick={() => frame.url && openLightbox(index)}
                      >
                        {frame.url ? (
                          <>
                            <img src={frame.url} alt={`Frame ${index + 1}`} className="w-full h-full object-cover" />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                              <Maximize2 className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                            </div>
                          </>
                        ) : frame.status === 'generating' ? (
                          <div className="w-full h-full flex items-center justify-center bg-zinc-800">
                            <Loader2 className="w-8 h-8 animate-spin text-yellow-500" />
                          </div>
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-zinc-800 text-zinc-600">
                            <ImageIcon className="w-8 h-8" />
                          </div>
                        )}
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-zinc-400">Frame {index + 1}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2"
                          onClick={(e) => { e.stopPropagation(); regenerateKeyframe(index); }}
                          disabled={generatingIndex !== null}
                        >
                          <RefreshCw className={`w-3 h-3 ${generatingIndex === index ? 'animate-spin' : ''}`} />
                        </Button>
                      </div>
                      <p className="text-xs text-zinc-500 line-clamp-2">{frame.description}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* PREVIEW MODE */}
        {mode === 'preview' && (
          <div className="space-y-6">
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Film className="w-5 h-5" />
                  Animation Preview
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="relative bg-black rounded-lg overflow-hidden mb-4" style={{ aspectRatio: '16/9' }}>
                  {finalVideoUrl ? (
                    <video 
                      src={finalVideoUrl} 
                      controls 
                      className="w-full h-full object-contain"
                      autoPlay
                      loop
                    />
                  ) : finalFrames[currentFrame] ? (
                    <img
                      src={finalFrames[currentFrame]}
                      alt={`Frame ${currentFrame}`}
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-zinc-600">
                      No frames yet
                    </div>
                  )}
                </div>

                {!finalVideoUrl && finalFrames.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-zinc-400 w-20">
                        {currentFrame + 1} / {finalFrames.length}
                      </span>
                      <Slider
                        value={[currentFrame]}
                        onValueChange={([v]) => setCurrentFrame(v)}
                        min={0}
                        max={finalFrames.length - 1}
                        step={1}
                        className="flex-1"
                      />
                      <span className="text-sm text-zinc-400 w-16 text-right">
                        {(currentFrame / 24).toFixed(2)}s
                      </span>
                    </div>

                    <div className="flex items-center justify-center gap-2">
                      <Button variant="outline" size="icon" onClick={() => setCurrentFrame(0)}>
                        <SkipBack className="w-4 h-4" />
                      </Button>
                      <Button
                        size="icon"
                        className="w-12 h-12"
                        onClick={() => setIsPlaying(!isPlaying)}
                      >
                        {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                      </Button>
                      <Button variant="outline" size="icon" onClick={() => setCurrentFrame(finalFrames.length - 1)}>
                        <SkipForward className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}

                {!finalVideoUrl && finalFrames.length > 0 && (
                  <Button 
                    className="w-full mt-4" 
                    size="lg"
                    onClick={compileVideo}
                    disabled={jobStatus === 'compiling'}
                  >
                    {jobStatus === 'compiling' ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Compiling Video...</>
                    ) : (
                      <><Film className="w-4 h-4 mr-2" /> Export as Video</>
                    )}
                  </Button>
                )}

                {finalVideoUrl && (
                  <div className="flex items-center gap-2 p-3 bg-green-900/30 rounded-lg">
                    <Check className="w-5 h-5 text-green-500" />
                    <span className="text-green-400">Video compiled successfully!</span>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* LIGHTBOX MODAL */}
        {lightboxOpen && generatedKeyframes[lightboxIndex] && (
          <div 
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
            onClick={closeLightbox}
          >
            {/* Close button */}
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 p-2 rounded-full bg-zinc-800 hover:bg-zinc-700 transition-colors z-10"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Frame counter */}
            <div className="absolute top-4 left-4 px-4 py-2 rounded-full bg-zinc-800 text-sm z-10">
              {lightboxIndex + 1} / {generatedKeyframes.length}
            </div>

            {/* Previous button */}
            <button
              onClick={(e) => { e.stopPropagation(); prevImage(); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-zinc-800 hover:bg-zinc-700 transition-colors z-10"
            >
              <ChevronLeft className="w-8 h-8" />
            </button>

            {/* Next button */}
            <button
              onClick={(e) => { e.stopPropagation(); nextImage(); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-zinc-800 hover:bg-zinc-700 transition-colors z-10"
            >
              <ChevronRight className="w-8 h-8" />
            </button>

            {/* Main image container */}
            <div 
              className="max-w-6xl max-h-[80vh] w-full mx-4 flex flex-col items-center"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Image */}
              <div className="relative w-full" style={{ aspectRatio: '16/9' }}>
                {generatedKeyframes[lightboxIndex].url ? (
                  <img
                    src={generatedKeyframes[lightboxIndex].url}
                    alt={`Frame ${lightboxIndex + 1}`}
                    className="w-full h-full object-contain rounded-lg"
                  />
                ) : generatedKeyframes[lightboxIndex].status === 'generating' ? (
                  <div className="w-full h-full flex items-center justify-center bg-zinc-900 rounded-lg">
                    <Loader2 className="w-12 h-12 animate-spin text-yellow-500" />
                  </div>
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-zinc-900 rounded-lg text-zinc-600">
                    <ImageIcon className="w-12 h-12" />
                  </div>
                )}
              </div>

              {/* Description and controls */}
              <div className="mt-4 w-full max-w-2xl text-center space-y-4">
                <p className="text-zinc-300 text-lg">
                  {generatedKeyframes[lightboxIndex].description}
                </p>
                <p className="text-zinc-500 text-sm">
                  Timestamp: {generatedKeyframes[lightboxIndex].timestamp}s
                </p>
                
                {/* Regenerate button */}
                <Button
                  onClick={regenerateFromLightbox}
                  disabled={generatingIndex !== null}
                  className="mt-2"
                  size="lg"
                >
                  {generatingIndex === lightboxIndex ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Regenerating...</>
                  ) : (
                    <><RefreshCw className="w-4 h-4 mr-2" /> Regenerate This Frame</>
                  )}
                </Button>
              </div>
            </div>

            {/* Thumbnail strip at bottom */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 p-2 bg-zinc-900/80 rounded-lg max-w-[90vw] overflow-x-auto">
              {generatedKeyframes.map((frame, index) => (
                <button
                  key={index}
                  onClick={(e) => { e.stopPropagation(); setLightboxIndex(index); }}
                  className={`w-16 h-10 rounded overflow-hidden border-2 shrink-0 transition-all ${
                    index === lightboxIndex 
                      ? 'border-purple-500 scale-110' 
                      : 'border-transparent opacity-60 hover:opacity-100'
                  }`}
                >
                  {frame.url ? (
                    <img src={frame.url} alt={`Thumb ${index + 1}`} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-zinc-800 flex items-center justify-center">
                      <span className="text-xs text-zinc-500">{index + 1}</span>
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
